import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ComposerStore } from './composerStore';

// ─── FSM state ────────────────────────────────────────────────────────────────

/**
 * Visual state of a single session dot.
 *
 * Priority (highest wins):
 *   active > recording > awaiting > idle > busy > default
 *
 * Transitions:
 *   any           → active    USER_SWITCH_TAB(sessionId)
 *   active        → default   USER_SWITCH_TAB(otherId)
 *   default/active→ busy      OUTBOUND_IN_FLIGHT (submitting/awaiting_stream/streaming)
 *   busy          → default   OUTBOUND_RESOLVED
 *   any non-active→ recording RECORDING_STARTED(sessionId)
 *   recording     → prior     RECORDING_STOPPED
 *   default/busy/idle→ awaiting  AGENT_QUESTION
 *   awaiting      → busy/default HUMAN_ANSWER_SUBMITTED
 *   any non-recording → idle  USER_LONG_PRESS + CONFIRM(sessionId) — 500 ms hold, then "Sleep?" dialog.
 *                              Works on the ACTIVE dot too: sleeping the current chat also auto-advances
 *                              to an adjacent non-slept session (or the list) — see pickSleepAdvanceTarget.
 *   idle          → default/busy/awaiting  USER_WAKE(sessionId) — focus it. Opening a slept session from
 *                              the recent list (or swiping to it) wakes it (the `current is never idle`
 *                              invariant, enforced by App's auto-wake-on-focus effect). This is intended.
 *   any           → (pruned)  SERVER_RECONCILE prunes stale sessionIds from idleSet
 *
 * Render policy: an `idle` dot is REMOVED from the bottom strip entirely (see
 * isDotHiddenFromStrip) — slept sessions stay reachable via the session list's
 * "recent" filter. They re-surface in the strip only when the agent needs the
 * operator, because `awaiting` outranks `idle` in the priority ladder above.
 */
export type AgentDotState =
  | 'active'
  | 'recording'
  | 'awaiting'
  | 'idle'
  | 'busy'
  | 'default';

// ─── Store interface ──────────────────────────────────────────────────────────

export interface AgentListStore {
  /**
   * Set of session IDs that the operator has explicitly paused via long-press.
   * This is the ONLY durable user-intent state owned by this store.
   * Everything else (active/busy/recording/awaiting) is derived from composerStore.
   */
  idleSet: Set<string>;

  /** Mark a session as idle (long-press gesture fired). */
  sleep: (sessionId: string) => void;

  /** Remove a session from the idle set (tap on idle dot). */
  wake: (sessionId: string) => void;

  /**
   * Remove any sessionIds from idleSet that the server no longer reports.
   * Called on SESSIONS_RECONCILE.
   */
  pruneStale: (serverSessionIds: string[]) => void;
}

// ─── Selector (pure function — no DOM) ───────────────────────────────────────

/**
 * Derives the visual dot state for a single session from two sources of truth:
 *   - composerStore snapshot (outbound queue, recording, orchestration gate)
 *   - idleSet from agentListStore (user's manual pause intent)
 *
 * This is a PURE FUNCTION — fully testable without a DOM or store subscription.
 */
export function getAgentDotState(
  sessionId: string,
  currentSessionId: string,
  composer: Pick<ComposerStore, 'sessions' | 'globalRecording' | 'orchestrationGated'>,
  idleSet: Set<string>,
): AgentDotState {
  // 1. Active — always wins; you're looking at it.
  if (sessionId === currentSessionId) return 'active';

  // 2. Recording — audio-in-progress signal.
  if (composer.globalRecording.sessionId === sessionId) return 'recording';

  const outbound = composer.sessions.get(sessionId)?.outbound ?? [];

  // 3. Awaiting human answer — agent is stuck; overrides idle because it needs input.
  const hasAwaiting = outbound.some((e) => e.mode === 'awaiting_human_answer');
  if (hasAwaiting) return 'awaiting';

  // 4. Idle — operator explicitly paused attention. Overrides busy (background work is fine).
  if (idleSet.has(sessionId)) return 'idle';

  // 5. Busy — outbound work in flight OR orchestration gate (handoff / context-reset).
  const hasBusy =
    outbound.some(
      (e) =>
        e.mode === 'submitting' ||
        e.mode === 'queued' ||
        e.mode === 'awaiting_stream' ||
        e.mode === 'streaming',
    ) || composer.orchestrationGated.has(sessionId);
  if (hasBusy) return 'busy';

  // 6. Default — nothing happening.
  return 'default';
}

// ─── Render policy ────────────────────────────────────────────────────────────

/**
 * Single source of truth for "should this dot be drawn in the bottom strip?".
 *
 * A session the operator put to sleep (`idle`) is hidden from the strip — it
 * stays reachable via the session list's "recent" filter. It re-surfaces only
 * when the agent needs the operator: the selector returns `awaiting` (which
 * outranks `idle`), and `awaiting` is NOT hidden. The component calls this
 * instead of inlining `state === 'idle'`, so the policy lives in the machine.
 */
export function isDotHiddenFromStrip(state: AgentDotState): boolean {
  return state === 'idle';
}

/**
 * When the operator sleeps the CURRENTLY ACTIVE chat, it leaves the strip and we
 * must move them somewhere. Pick the nearest *non-slept* neighbour in tab order
 * (search outward, right first then left); return `null` when there is no other
 * non-slept session, in which case the caller navigates to the session list.
 *
 * Pure + greppable so it can be exercised by the simulator without a DOM.
 * `idleSet` here is the set BEFORE adding `sleepingId` — we exclude `sleepingId`
 * explicitly and skip any session already slept.
 */
export function pickSleepAdvanceTarget(
  openTabs: string[],
  sleepingId: string,
  idleSet: Set<string>,
): string | null {
  const idx = openTabs.indexOf(sleepingId);
  if (idx === -1) return null;
  for (let d = 1; d < openTabs.length; d++) {
    const right = openTabs[idx + d];
    if (right && right !== sleepingId && !idleSet.has(right)) return right;
    const left = openTabs[idx - d];
    if (left && left !== sleepingId && !idleSet.has(left)) return left;
  }
  return null;
}

// ─── Visible dot set (Phase 3 — swipe carousel ≡ dot strip) ──────────────────
//
// The session-dots strip and the swipe carousel MUST traverse the SAME set of
// sessions: the *visible* (non-slept) ones. Before Phase 3 the dots filtered slept
// agents via isDotHiddenFromStrip while the swipe hook cycled over the raw openTabs,
// so a swipe could land on a slept agent that had no dot (and silently wake it via the
// "current is never idle" invariant). These helpers are the single source of truth for
// "which agents are dot/swipe candidates" — both consumers read this, so they can't
// drift apart.
//
// CRITICAL: visibility is decided by the FULL getAgentDotState → isDotHiddenFromStrip
// pipeline, NOT a bare `!idleSet.has(id)`. A slept agent that raises an agent question
// resolves to `awaiting` (which outranks `idle`), and `awaiting` is NOT hidden — so a
// slept-but-awaiting agent is correctly RE-INCLUDED in the visible/swipe set. The
// active session is never hidden (current → 'active'), so it is always present; callers
// indexing the current session into this set never get -1.

export interface VisibleDot {
  tabId: string;
  dotState: AgentDotState;
}

/**
 * The dots to draw in the strip, in tab order: each open tab paired with its derived
 * dotState, with slept (hidden) tabs removed. Pure — no DOM, no store subscription.
 */
export function getVisibleDots(
  openTabs: string[],
  currentSessionId: string,
  composer: Pick<ComposerStore, 'sessions' | 'globalRecording' | 'orchestrationGated'>,
  idleSet: Set<string>,
): VisibleDot[] {
  return openTabs
    .map((tabId) => ({
      tabId,
      dotState: getAgentDotState(tabId, currentSessionId, composer, idleSet),
    }))
    .filter((d) => !isDotHiddenFromStrip(d.dotState));
}

/**
 * The visible tab IDs (the set the swipe carousel + slivers cycle through). Derived
 * from getVisibleDots so the swipe set is provably the same set the dots show.
 */
export function getVisibleDotTabs(
  openTabs: string[],
  currentSessionId: string,
  composer: Pick<ComposerStore, 'sessions' | 'globalRecording' | 'orchestrationGated'>,
  idleSet: Set<string>,
): string[] {
  return getVisibleDots(openTabs, currentSessionId, composer, idleSet).map((d) => d.tabId);
}

// ─── Persist serialization helpers ───────────────────────────────────────────
// Set<string> does not JSON round-trip natively; serialize as a plain string[].

function setReplacer(_key: string, value: unknown): unknown {
  if (value instanceof Set) {
    return { __type: 'Set', items: [...(value as Set<unknown>)] };
  }
  return value;
}

function setReviver(_key: string, value: unknown): unknown {
  if (
    value !== null &&
    typeof value === 'object' &&
    (value as Record<string, unknown>).__type === 'Set'
  ) {
    return new Set((value as { items: string[] }).items);
  }
  return value;
}

const setAwareStorage = createJSONStorage(() => localStorage, {
  replacer: setReplacer,
  reviver: setReviver,
});

// ─── Store creation ───────────────────────────────────────────────────────────

export const useAgentListStore = create<AgentListStore>()(
  persist(
    (set) => ({
      idleSet: new Set<string>(),

      sleep: (sessionId) =>
        set((state) => {
          const next = new Set(state.idleSet);
          next.add(sessionId);
          return { idleSet: next };
        }),

      wake: (sessionId) =>
        set((state) => {
          const next = new Set(state.idleSet);
          next.delete(sessionId);
          return { idleSet: next };
        }),

      pruneStale: (serverSessionIds) =>
        set((state) => {
          const serverSet = new Set(serverSessionIds);
          const next = new Set<string>();
          for (const id of state.idleSet) {
            if (serverSet.has(id)) next.add(id);
          }
          return { idleSet: next };
        }),
    }),
    {
      name: 'agent-list-store-v1',
      storage: setAwareStorage,
    },
  ),
);
